import React from "react";
import { Link } from "react-router";

import type { ColumnDef } from "@tanstack/react-table";

import { LOCALES } from "@/components/enrichment/config";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import type { AreaListItem } from "@/types/area";

/**
 * `partNames` maps a part id to its name, for the Part column. The Venues column shows a row's
 * own count: Typesense holds no children sum, so a part's figure leaves out its child areas.
 */
export const useAreaColumns = (partNames: ReadonlyMap<string, string>) =>
  React.useMemo<ColumnDef<AreaListItem>[]>(() => {
    return [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        size: 220,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Link
              to={`/areas/edit/${encodeURIComponent(row.original.id)}`}
              className="truncate font-medium hover:underline"
            >
              {row.original.name}
            </Link>
            {row.original.kind === "part" && (
              <Badge variant="secondary">Part</Badge>
            )}
          </div>
        ),
      },
      {
        id: "city",
        accessorKey: "city",
        header: "City",
        size: 160,
        enableSorting: true,
        cell: ({ row }) => `${row.original.city}, ${row.original.countryCode}`,
      },
      {
        id: "part",
        header: "Part",
        size: 160,
        enableSorting: false,
        cell: ({ row }) =>
          (row.original.parentId && partNames.get(row.original.parentId)) ||
          "—",
      },
      {
        id: "venueCount",
        accessorKey: "venueCount",
        header: () =>
          makeTooltip(
            <span>Venues</span>,
            "A part shows its own venues only, not its child areas' venues.",
          ),
        size: 90,
        enableSorting: true,
        cell: ({ row }) => row.original.venueCount,
      },
      {
        id: "aliases",
        header: "Aliases",
        size: 90,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.aliases.length === 0
            ? 0
            : makeTooltip(
                <span>{row.original.aliases.length}</span>,
                row.original.aliases.join(", "),
              ),
      },
      {
        id: "displayNames",
        header: "Display names",
        size: 120,
        enableSorting: false,
        cell: ({ row }) =>
          `${Object.values(row.original.displayNames).filter(Boolean).length} / ${LOCALES.length}`,
      },
      {
        id: "origin",
        accessorKey: "origin",
        header: "Origin",
        size: 100,
        // `origin` is a filter field in Typesense, not a sort field.
        enableSorting: false,
        cell: ({ row }) =>
          row.original.origin ? (
            <Badge variant="outline">{row.original.origin}</Badge>
          ) : (
            "—"
          ),
      },
      {
        id: "updatedAt",
        accessorKey: "updatedAt",
        header: "Updated",
        size: 120,
        enableSorting: true,
        cell: ({ row }) =>
          row.original.updatedAt
            ? new Date(row.original.updatedAt).toLocaleDateString()
            : "—",
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: ({ row }) => (
          <EditButton
            size="sm"
            hideText={true}
            recordItemId={row.original.id}
          />
        ),
      },
    ];
  }, [partNames]);
